import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckSvg from '../assets/landing-page/check.svg';
import { useWithTokenRefresh } from '../utils/api';
import { useApi } from '../contexts/ApiContext';

// Credits API response types (match FlashbackClient credits API)
interface CreditPackItem {
  id: string;
  name: string;
  code: string;
  credits: number;
  price: number;
}
interface GetCreditsBalanceResponse {
  success: boolean;
  data?: { realtimeBalance: number; creditBalance: number; pendingConsumption: number; breakdown: { storage: number; ai: number } };
}
interface GetCreditsPacksResponse {
  success: boolean;
  data?: CreditPackItem[];
}
interface BuyCreditsPackResponse {
  success: boolean;
  checkoutUrl?: string | null;
  message?: string;
  nextAvailableAt?: string;
}

// Set to false to remove the blur effect on Pay-as-you-need section
const BLUR_PAY_AS_YOU_NEED = true;

interface PricingPlanProps {
  name: string;
  price: string;
  definition: string;
  features: string[];
  isPopular?: boolean;
  buttonText?: string;
  isBlurred?: boolean;
  onButtonClick?: () => void;
  disabled?: boolean;
  isCurrentPlan?: boolean;
}

const PricingPlan: React.FC<PricingPlanProps> = ({
  name,
  price,
  definition,
  features,
  isPopular = false,
  buttonText = 'Get started',
  isBlurred = false,
  onButtonClick,
  disabled = false,
  isCurrentPlan = false,
}) => {
  const handleClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
  };
  return (
    <div className="relative">
      {isPopular && (
        <div className="absolute -top-9 left-0 right-0 flex justify-center z-10 w-full md:hidden">
          <div
            className="text-white text-center text-sm font-medium w-full flex items-center justify-center"
            style={{
              backgroundColor: '#617BFF',
              height: '36px',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            }}
          >
            Popular
          </div>
        </div>
      )}
      <div
        className={`flex flex-col relative ${isPopular ? 'border-t-0 rounded-t-none' : ''}`}
        style={{ borderRadius: isPopular ? '0 0 8px 8px' : '8px' }}
      >
        <div className="p-6 flex flex-col flex-grow">
          <div className="flex justify-center mb-4">
            <div
              className="text-sm text-gray-600 py-1 px-2.5 border border-gray-200 rounded-lg"
              style={{ padding: '4px 10px', borderRadius: '8px', borderWidth: '1px' }}
            >
              {name}
            </div>
          </div>
          {/* Price display */}
          <div className="flex justify-center items-baseline mb-1">
            <span
              className="text-center"
              style={{
                fontWeight: 600,
                fontSize: '48px',
                lineHeight: '60px',
                letterSpacing: '-0.02em',
              }}
            >
              {price}
            </span>
          </div>
          <div className="text-sm text-gray-600 mb-6 text-center">{definition}</div>

          <div className="flex justify-center">
            {isCurrentPlan ? (
              <div
                className="w-full py-2.5 px-4 text-sm font-medium text-center"
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#6B7280',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                Current Plan
              </div>
            ) : (
              <button
                onClick={handleClick}
                disabled={disabled}
                className={`w-full py-2.5 px-4 text-sm font-medium transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
                  isPopular
                    ? 'text-white'
                    : 'bg-white text-black border border-gray-300 hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: isPopular ? '#617BFF' : '',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 1px 2px 0px rgba(10, 13, 18, 0.03), 0px -1px 0px 0px rgba(10, 13, 18, 0.02) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.08) inset',
                }}
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="w-3/4 mx-auto border-t border-gray-200 mb-4"></div>
          {features.map((feature, index) => (
            <div key={index} className="flex items-start py-1">
              <div
                className="flex items-center justify-center mr-3 flex-shrink-0"
                style={{ width: '20px', height: '20px' }}
              >
                <img src={CheckSvg} alt="Check" width="20" height="20" />
              </div>
              <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface EnterpriseCardProps {
  title: string;
  description: string;
  features: string[];
  buttonText: string;
  onButtonClick?: () => void;
  showCapacityPricing?: boolean;
  onCapacityGetStartedClick?: () => void;
}

const EnterpriseCard: React.FC<EnterpriseCardProps> = ({
  title,
  description,
  features,
  buttonText,
  onButtonClick,
  showCapacityPricing = false,
  onCapacityGetStartedClick,
}) => {
  return (
    <div className="flex flex-col">
      <div
        className="border border-gray-200 p-6 flex flex-col md:flex-row justify-between items-start gap-6"
        style={{ borderRadius: '12px' }}
      >
        <div>
          <h3 className="text-xl font-bold mb-2">{title}</h3>
          <p className="text-gray-600 mb-4">{description}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="flex items-center justify-center mr-3 flex-shrink-0"
                  style={{ width: '20px', height: '20px' }}
                >
                  <img src={CheckSvg} alt="Check" width="20" height="20" />
                </div>
                <span className="text-sm text-gray-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="shrink-0 self-start">
          <button
            className="py-2 px-6 text-sm font-medium text-white"
            style={{
              backgroundColor: '#617BFF',
              borderRadius: '8px',
              boxShadow:
                '0px 1px 2px 0px rgba(10, 13, 18, 0.03), 0px -1px 0px 0px rgba(10, 13, 18, 0.02) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.08) inset',
            }}
            onClick={onButtonClick}
          >
            {buttonText}
          </button>
        </div>
      </div>

      {/* COMMENTED OUT: Pay-as-you-need section */}
      {false && showCapacityPricing && (
        <div className="mt-36 relative">
          {/* Pay-as-you-need title - always visible */}
          <h3 className="text-lg font-medium mb-2">Pay-as-you-need: Additional Traffic Capacity</h3>

          {/* Pay-as-you-need content - blurred when disabled */}
          <div
            style={
              BLUR_PAY_AS_YOU_NEED
                ? { filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' }
                : {}
            }
          >
            <p className="text-gray-600 text-sm mb-6">
              Flashgate can scale as you grow. Purchase additional traffic credits that don't expire
              at the end of your subscription period.
            </p>

            <h4 className="text-base font-medium mb-4">Private Bridge Node</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium text-sm text-gray-600">
                      Additional capacity
                    </td>
                    <td className="py-3 px-4 text-sm">1TB</td>
                    <td className="py-3 px-4 text-sm">10TB</td>
                    <td className="py-3 px-4 text-sm">50TB</td>
                    <td className="py-3 px-4 text-sm">100TB</td>
                    <td className="py-3 px-4 text-sm">250TB</td>
                    <td className="py-3 px-4 text-sm">500TB</td>
                  </tr>
                  <tr style={{ backgroundColor: '#FAFAFA' }}>
                    <td
                      className="pb-1 pt-3 pr-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      Price
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $15
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $120
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $550
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $1,000
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $2,250
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $4,000
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#FAFAFA' }}>
                    <td
                      className="pt-1 pb-3 pr-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      Per Terabyte
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $15/TB
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $12/TB
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $11/TB
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $10/TB
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $9/TB
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $8/TB
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="text-base font-medium mb-4 mt-12">Public Bridge Node</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4 font-medium text-sm text-gray-600">
                      Additional capacity
                    </td>
                    <td className="py-3 px-4 text-sm">1TB</td>
                  </tr>
                  <tr style={{ backgroundColor: '#FAFAFA' }}>
                    <td
                      className="pb-1 pt-3 pr-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      Price
                    </td>
                    <td
                      className="pb-1 pt-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                      }}
                    >
                      $120
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#FAFAFA' }}>
                    <td
                      className="pt-1 pb-3 pr-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      Per Terabyte
                    </td>
                    <td
                      className="pt-1 pb-3 px-4"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#535862',
                      }}
                    >
                      $120/TB
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6">
              <button
                className="py-2 px-6 text-sm font-medium text-white"
                style={{
                  backgroundColor: '#617BFF',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 1px 2px 0px rgba(10, 13, 18, 0.03), 0px -1px 0px 0px rgba(10, 13, 18, 0.02) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.08) inset',
                }}
                onClick={onCapacityGetStartedClick ?? onButtonClick}
              >
                Get started
              </button>
            </div>
          </div>

          {/* Coming Soon overlay */}
          {BLUR_PAY_AS_YOU_NEED && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  backgroundColor: '#617BFF',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  fontSize: '24px',
                  fontWeight: 600,
                  boxShadow: '0 10px 40px rgba(97, 123, 255, 0.3)',
                  textAlign: 'center',
                }}
              >
                Coming Soon
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PricingSectionProps {
  showStripeLinks?: boolean;
  currentPlan?: string;
}

const PricingSection: React.FC<PricingSectionProps> = ({ showStripeLinks, currentPlan }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [useCreditsMode, setUseCreditsMode] = useState(false);
  const [creditPacks, setCreditPacks] = useState<CreditPackItem[]>([]);
  const [creditPacksLoading, setCreditPacksLoading] = useState(false);
  const withTokenRefresh = useWithTokenRefresh();
  const { apiClient } = useApi();

  // Plan hierarchy for upgrade/downgrade logic (includes credit-based tier names: Free, Starter, Growth, Scale)
  const planHierarchy: Record<string, number> = {
    'free trial': 0,
    'free': 0,
    'starter': 1,
    'pro': 2,
    'growth': 2,
    'business': 3,
    'scale': 3,
    'enterprise': 4,
  };

  // Get button text based on plan comparison
  const getButtonText = (planName: string): string => {
    if (!currentPlan) return 'Get started';
    
    const currentLevel = planHierarchy[currentPlan.toLowerCase()] ?? -1;
    const planLevel = planHierarchy[planName.toLowerCase()] ?? -1;
    
    if (currentLevel < 0 || planLevel < 0) return 'Get started';
    if (currentLevel === planLevel) return 'Current Plan';
    
    return planLevel > currentLevel ? 'Upgrade' : 'Downgrade';
  };

  // Open signup in a new tab but keep browser back to landing available in that tab
  const openSignupInNewTabPreservingBack = () => {
    window.open('/auth?tab=signup', '_blank');
  };

  const openContactInNewTab = () => {
    window.open('https://calendar.app.google/CLYhgQzzZhBYSter5', '_blank');
  };

  // Map UI plan names to API names (backend uses Starter, Growth, Scale)
  const planNameToApiNames = (planName: string): string[] => {
    const lower = planName.toLowerCase();
    if (lower === 'growth') return ['growth', 'pro'];
    if (lower === 'scale') return ['scale', 'business'];
    return [lower];
  };

  // Find subscription in API data: prefer exact name match so we use Growth/Scale when backend
  // returns both legacy (Pro, Business) and new credit tiers (Growth, Scale), avoiding wrong prices.
  const findPlan = (planName: string): typeof subscriptions[0] | undefined => {
    const apiNames = planNameToApiNames(planName);
    const exact = subscriptions.find(s => s.name.toLowerCase() === planName.toLowerCase());
    if (exact) return exact;
    return subscriptions.find(s => apiNames.includes(s.name.toLowerCase()));
  };

  // Helper function to get plan price from API data
  const getPlanPrice = (planName: string): string => {
    if (subscriptions.length === 0) {
      // Fallback prices while loading or if API fails
      const fallbackPrices: Record<string, { monthly: number; yearly: number }> = {
        'Starter': { monthly: 99.99, yearly: 999.99 },
        'Growth': { monthly: 299.99, yearly: 2999.99 },
        'Scale': { monthly: 999.99, yearly: 9999.99 },
      };
      const fallback = fallbackPrices[planName];
      if (fallback) {
        const price = billingPeriod === 'yearly' ? fallback.yearly : fallback.monthly;
        const n = Number(price);
        return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
      }
      return '$0';
    }

    const plan = findPlan(planName);
    if (!plan || !plan.periods) {
      return '$0';
    }

    // Find the period (MONTHLY or YEARLY)
    const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
    const period = plan.periods.find((p: any) => p.periodType === periodType);
    if (!period) {
      console.warn(`[PricingSection] Period "${periodType}" not found for plan "${planName}"`);
      return '$0';
    }

    // Return formatted price from API (monthly = period price, yearly = full yearly price)
    const price = typeof period.price === 'number' ? period.price : Number(period.price);
    const n = Number(price);
    return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
  };

  // Detect credits-based billing (backend SUBSCRIPTION_SYSTEM=credits)
  React.useEffect(() => {
    if (!showStripeLinks) return;
    const detectCreditsMode = async () => {
      try {
        const balanceResp = await withTokenRefresh(() => (apiClient as any).getCreditsBalance()) as GetCreditsBalanceResponse;
        if (balanceResp.success) {
          setUseCreditsMode(true);
        }
      } catch {
        // Backend not in credits mode or unauthenticated
      }
    };
    detectCreditsMode();
  }, [showStripeLinks, withTokenRefresh, apiClient]);

  // Load credit packs when in credits mode (in-app only)
  React.useEffect(() => {
    if (!useCreditsMode || !showStripeLinks) return;
    let mounted = true;
    const loadPacks = async () => {
      try {
        setCreditPacksLoading(true);
        const response = await withTokenRefresh(() => (apiClient as any).getCreditsPacks()) as GetCreditsPacksResponse;
        if (mounted && response.success && response.data) {
          setCreditPacks(response.data);
        }
      } catch (err) {
        console.error('[PricingSection] Failed to load credit packs:', err);
      } finally {
        if (mounted) setCreditPacksLoading(false);
      }
    };
    loadPacks();
    return () => { mounted = false; };
  }, [useCreditsMode, showStripeLinks, withTokenRefresh, apiClient]);

  // Charger les subscriptions depuis l'API (pour afficher les prix dynamiquement)
  React.useEffect(() => {
    // Charger l'API publique /subscriptions pour obtenir les prix
    const loadPrices = async () => {
      setSubscriptionsLoading(true);
      try {
        const response = await withTokenRefresh(() => apiClient.getSubscriptions());
        if (response.success && response.data) {
          console.log('[PricingSection] Loaded subscriptions with prices:', response.data);
          setSubscriptions(response.data);
          // When catalog has creditsGranted, use credits mode (so modal shows correct features + packs)
          const hasCreditsCatalog = (response.data as any[]).some(
            (s: any) => s.periods?.some((p: any) => p.creditsGranted != null)
          );
          if (hasCreditsCatalog) {
            setUseCreditsMode(true);
          }
        }
      } catch (err) {
        console.error('[PricingSection] Failed to load subscriptions:', err);
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    loadPrices();
  }, []);

  // Créer une session Stripe via le backend
  const handleBuySubscription = async (planName: string) => {
    if (!showStripeLinks) return; // Seulement dans l'app
    
    try {
      setLoading(true);
      console.log('[PricingSection] Starting checkout for plan:', planName);
      console.log('[PricingSection] Available subscriptions:', subscriptions);
      
      const plan = findPlan(planName);
      if (!plan) {
        console.error('[PricingSection] Plan not found:', planName);
        alert(`Plan "${planName}" not found. Please try again.`);
        return;
      }
      
      console.log('[PricingSection] Found plan:', plan);
      
      // Trouver la période correspondante
      const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
      const period = plan.periods?.find((p: any) => p.periodType === periodType);
      if (!period) {
        console.error('[PricingSection] Period not found for plan:', planName, periodType);
        alert(`Period type "${periodType}" not available for this plan.`);
        return;
      }
      
      console.log('[PricingSection] Found period:', period);
      console.log('[PricingSection] Calling API with periodId:', period.id);
      
      // Appeler l'API pour créer la session Stripe (comme ProfileModal)
      const response = await withTokenRefresh(() =>
        apiClient.buySubscription({ subscriptionPeriodId: period.id })
      );
      
      console.log('[PricingSection] API response:', response);
      
      if (response.success) {
        if (response.checkoutUrl) {
          // Paid subscription - open Stripe Checkout in a new window
          console.log('[PricingSection] Opening checkout URL:', response.checkoutUrl);
          window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
        } else {
          // Free subscription - checkoutUrl is undefined
          alert('Subscription purchased successfully!');
        }
      } else {
        console.error('[PricingSection] Failed to create checkout session:', response);
        alert(response.message || 'Failed to process subscription. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      
      // Handle specific error cases
      if (error?.status === 409) {
        alert('You already have an active subscription. Please cancel your current subscription before upgrading to a new plan.');
      } else if (error?.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert('Failed to start checkout. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Buy credit pack (credits mode) -> Stripe Checkout
  const handleBuyCreditsPack = async (packId: string) => {
    if (!showStripeLinks) return;
    try {
      setLoading(true);
      const response = await withTokenRefresh(() => (apiClient as any).buyCreditsPack({ packId })) as BuyCreditsPackResponse;
      if (response.success && response.checkoutUrl) {
        window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else if (response.success && !response.checkoutUrl) {
        alert('Credit pack purchased successfully.');
      } else {
        alert(response.message || response.nextAvailableAt ? 'Purchase limit reached. Try again later.' : 'Failed to start checkout.');
      }
    } catch (error: any) {
      console.error('[PricingSection] Buy credits pack error:', error);
      alert(error?.message || 'Failed to start checkout.');
    } finally {
      setLoading(false);
    }
  };

  // Get credits granted for a plan (for display in credits mode)
  const getPlanCredits = (planName: string): number | null => {
    const plan = findPlan(planName);
    const periodType = billingPeriod === 'yearly' ? 'YEARLY' : 'MONTHLY';
    const period = plan?.periods?.find((p: any) => p.periodType === periodType);
    return period?.creditsGranted ?? null;
  };

  // Texte des fonctionnalités pour chaque plan
  const freePlanFeatures = [
    '15GB for all (Public nodes)',
    '3 provider integrations',
    '5 buckets & 2 repositories',
    'API Calls',
    'Statistics and Reports',
    '1 email access per org',
  ];

  const starterPlanFeatures = [
    '1TB traffic allowance/month (Private nodes)',
    '15GB for all (Public nodes)',
    '3 provider integrations',
    '3 Private bridge nodes',
    '25 buckets & 5 repositories',
    'API Calls',
    'Statistics and Reports',
    '1 email access per org',
    'Pay-as-you-need for additional traffic',
  ];

  const proPlanFeatures = [
    '5TB traffic allowance/month (Private nodes)',
    '15GB for all (Public nodes)',
    '5 provider integrations',
    '10 Private bridge nodes',
    '50 buckets & 10 repositories',
    'API Calls',
    'Statistics and Reports',
    '3 email access per org',
    'Pay-as-you-need for additional traffic',
  ];

  const businessPlanFeatures = [
    '35TB traffic allowance/month (Private nodes)',
    '15GB for all (Public nodes)',
    'Unlimited provider integrations',
    'Unlimited buckets & repositories',
    '50 Private bridge nodes',
    'API Calls',
    'Statistics and Reports',
    '5 email access per org',
    'Pay-as-you-need for additional traffic',
  ];

  // Credits-based billing: only show credits + short feature list (no old resource numbers)
  const creditsModePlanFeatures = ['API Calls', 'Statistics and Reports', 'Unlimited users email access per org'];

  return (
    <div id="pricing-section" className="w-full bg-white py-16">
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-2">Simple, transparent pricing</h2>
          <p className="text-gray-600">
            We believe Flashgate should be accessible to all companies, no matter the size.
          </p>

          <div className="flex justify-center mt-6 mb-10">
            <div
              className="flex rounded-lg"
              style={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#FFFFFF',
                width: '200px',
                height: '40px',
                padding: '0px',
                gap: '8px',
                overflow: 'visible',
              }}
            >
              <button
                className="text-sm transition-all"
                style={{
                  backgroundColor: billingPeriod === 'monthly' ? '#F3F4F6' : 'transparent',
                  fontWeight: billingPeriod === 'monthly' ? '500' : '400',
                  boxShadow: billingPeriod === 'monthly' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none',
                  border: billingPeriod === 'monthly' ? '1px solid #E5E7EB' : 'none',
                  borderRadius: billingPeriod === 'monthly' ? '6px' : '0',
                  width: '90px',
                  height: '38px',
                  padding: '0px',
                  margin: '0px',
                }}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button
                className="text-sm transition-all relative"
                style={{
                  backgroundColor: billingPeriod === 'yearly' ? '#F3F4F6' : 'transparent',
                  fontWeight: billingPeriod === 'yearly' ? '500' : '400',
                  boxShadow: billingPeriod === 'yearly' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none',
                  border: billingPeriod === 'yearly' ? '1px solid #E5E7EB' : 'none',
                  borderRadius: billingPeriod === 'yearly' ? '6px' : '0',
                  width: '90px',
                  height: '38px',
                  padding: '0px',
                  margin: '0px',
                }}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly
                <span
                  className="absolute text-xs font-semibold"
                  style={{
                    top: '-6px',
                    right: '2px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    lineHeight: '12px',
                  }}
                >
                  20%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="relative flex flex-col md:flex-row flex-wrap justify-center mt-16">
          {subscriptionsLoading && showStripeLinks ? (
            <div className="flex justify-center items-center py-16 w-full">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-[#617BFF]" />
                <span className="text-sm text-gray-500">Loading plans...</span>
              </div>
            </div>
          ) : (
          <div className="w-full flex flex-col md:flex-row justify-center space-y-6 md:space-y-0 md:space-x-0 max-w-[912px] mx-auto md:border md:border-gray-200 md:rounded-lg overflow-hidden md:overflow-visible">
            {/* Free Plan - COMMENTED OUT */}
            {false && (
              <div className="w-full md:w-[304px] md:border-r md:border-gray-200">
                <PricingPlan
                  name="Free plan"
                  price="$0"
                  definition="For devs, testing, solo hackers"
                  features={freePlanFeatures}
                  onButtonClick={openSignupInNewTabPreservingBack}
                />
              </div>
            )}

            {/* Starter Plan */}
            <div className="w-full md:w-[304px] md:border-r md:border-gray-200">
              <PricingPlan
                name="Starter"
                price={getPlanPrice('Starter')}
                definition="For early-stage teams"
                features={
                  useCreditsMode
                    ? [
                        ...(getPlanCredits('Starter') != null ? [`${getPlanCredits('Starter')} credits per ${billingPeriod === 'yearly' ? 'year' : 'month'}`] : []),
                        ...creditsModePlanFeatures,
                      ]
                    : starterPlanFeatures
                }
                buttonText={getButtonText('Starter')}
                onButtonClick={showStripeLinks === undefined ? openSignupInNewTabPreservingBack : (showStripeLinks === true ? () => handleBuySubscription('Starter') : undefined)}
                disabled={loading}
                isCurrentPlan={['starter'].includes(currentPlan?.toLowerCase() ?? '')}
              />
            </div>

            {/* Growth Plan (formerly Pro) */}
            <div className="w-full md:w-[304px] md:border-r md:border-gray-200 relative">
              {/* Popular Banner (desktop only) */}
              <div className="hidden md:flex absolute -top-9 left-0 right-0 z-10">
                <div
                  className="text-white text-sm font-medium flex items-center justify-center"
                  style={{
                    width: '304px',
                    height: '36px',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    paddingTop: '8px',
                    paddingRight: '12px',
                    paddingBottom: '8px',
                    paddingLeft: '12px',
                    gap: '4px',
                    opacity: 1,
                    backgroundColor: '#617BFF',
                    margin: '0 auto',
                  }}
                >
                  Popular
                </div>
              </div>
              <PricingPlan
                name="Growth"
                price={getPlanPrice('Growth')}
                definition="For growing teams"
                isPopular={true}
                features={
                  useCreditsMode
                    ? [
                        ...(getPlanCredits('Growth') != null ? [`${getPlanCredits('Growth')} credits per ${billingPeriod === 'yearly' ? 'year' : 'month'}`] : []),
                        ...creditsModePlanFeatures,
                      ]
                    : proPlanFeatures
                }
                buttonText={getButtonText('Growth')}
                onButtonClick={showStripeLinks === undefined ? openSignupInNewTabPreservingBack : (showStripeLinks === true ? () => handleBuySubscription('Growth') : undefined)}
                disabled={loading}
                isCurrentPlan={['growth'].includes(currentPlan?.toLowerCase() ?? '')}
              />
            </div>

            {/* Scale Plan (formerly Business) */}
            <div className="w-full md:w-[304px]">
              <PricingPlan
                name="Scale"
                price={getPlanPrice('Scale')}
                definition="Production-scale usage"
                features={
                  useCreditsMode
                    ? [
                        ...(getPlanCredits('Scale') != null ? [`${getPlanCredits('Scale')} credits per ${billingPeriod === 'yearly' ? 'year' : 'month'}`] : []),
                        ...creditsModePlanFeatures,
                      ]
                    : businessPlanFeatures
                }
                buttonText={getButtonText('Scale')}
                onButtonClick={showStripeLinks === undefined ? openSignupInNewTabPreservingBack : (showStripeLinks === true ? () => handleBuySubscription('Scale') : undefined)}
                disabled={loading}
                isCurrentPlan={['scale'].includes(currentPlan?.toLowerCase() ?? '')}
              />
            </div>
          </div>
          )}
        </div>

        {/* Credit packs (credits-based billing, in-app only) */}
        {useCreditsMode && showStripeLinks && (
          <div className="mt-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Buy more credits</h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              One-time credit packs. Use for storage egress and AI usage.
            </p>
            {creditPacksLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-[#617BFF]" />
              </div>
            ) : creditPacks.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-4">
                {creditPacks.map((pack) => (
                  <div
                    key={pack.id}
                    className="border border-gray-200 rounded-lg p-4 min-w-[160px] text-center"
                    style={{ borderRadius: '8px' }}
                  >
                    <div className="font-semibold text-gray-900">{pack.name}</div>
                    <div className="text-2xl font-bold text-[#101828] mt-1">{pack.credits} credits</div>
                    <div className="text-sm text-gray-600 mt-0.5">${pack.price}</div>
                    <button
                      type="button"
                      onClick={() => handleBuyCreditsPack(pack.id)}
                      disabled={loading}
                      className="mt-3 w-full py-2 px-4 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                      style={{ backgroundColor: '#617BFF' }}
                    >
                      Buy
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center">No credit packs available.</p>
            )}
          </div>
        )}

        {/* Landing page CTAs */}
        {showStripeLinks === undefined && (
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
            <button
              onClick={openSignupInNewTabPreservingBack}
              className="text-white font-medium text-sm rounded px-8 py-3 hover:bg-blue-700 transition-colors"
              style={{
                backgroundColor: '#617BFF',
                borderRadius: '8px',
                boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.03), 0px -1px 0px 0px rgba(10, 13, 18, 0.02) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.08) inset',
              }}
            >
              Sign up free
            </button>
            <button
              onClick={openContactInNewTab}
              className="font-medium text-sm rounded px-8 py-3 hover:bg-gray-50 transition-colors"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#414651',
                borderRadius: '8px',
                border: '1px solid #D5D7DA',
                boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.03), 0px -1px 0px 0px rgba(10, 13, 18, 0.02) inset, 0px 0px 0px 1px rgba(10, 13, 18, 0.08) inset',
              }}
            >
              Request a Demo
            </button>
          </div>
        )}

        {/* Enterprise Card */}
        <div className="mt-12 max-w-[1200px] mx-auto px-4">
          <EnterpriseCard
            title="Enterprise"
            description="We believe Flashgate should be accessible to all companies, no matter the size."
            features={[
              'Everything in business',
              'Custom pricing',
              'Custom seating',
              'Dedicated support',
            ]}
            buttonText="Contact us"
            onButtonClick={openContactInNewTab}
            showCapacityPricing={true}
            onCapacityGetStartedClick={openSignupInNewTabPreservingBack}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
